import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Skeleton } from '@kilat/ui';
import type { LifetimeStats } from '@kilat/shared';

import { useI18n } from '../lib/use-i18n.js';
import { logger } from '../lib/logger.js';
import { lifetimeStatsRepoFor } from '../lib/repositories/lifetime-stats-repo.js';

/**
 * Regulars — how many of this operator's customers have come back.
 *
 * ## Why this exists
 *
 * `RetentionPulseCard` on `/marketing` already shows the LOSS side: who has
 * gone quiet and what that is worth. Nothing showed the other side — what the
 * operator has actually built. An operator deciding whether to keep paying for
 * this asks "what do I have here?", and until now the product could not answer
 * with a number.
 *
 * ## Why this number and not "RM brought back"
 *
 * The tempting card is "your loyalty program brought back RM X". That figure
 * cannot be computed honestly from what is stored: attributing a specific
 * ringgit to the loyalty program means knowing which repeat visits would not
 * have happened otherwise, which nothing here records. Inventing a plausible
 * number would be a fabricated milestone, and this product does not do those.
 *
 * What IS true and cheap: a customer either came back or did not.
 * `repeatCustomerCount` is incremented by `onFirstRepeatCustomer` on the
 * visits 1→2 transition — once per customer, ever — and sits in the same
 * `stats/lifetime` document as `customerCount`. One document read gives both,
 * which is what the free-tier budget can afford on a screen an operator opens
 * daily. Every number on this card is counted, not modelled.
 *
 * ## States
 *
 * Loading, load-failure (silent — this is a supporting card and must never
 * replace the reports page with an error), a genuine "no regulars yet" state
 * that says what will make one appear, and the populated state.
 */
export function RegularsCard({ tenantId }: { readonly tenantId: string }) {
  const t = useI18n();
  const [stats, setStats] = useState<LifetimeStats | null | 'loading'>('loading');

  useEffect(() => {
    let cancelled = false;
    lifetimeStatsRepoFor(tenantId)
      .get('lifetime')
      .then((doc) => {
        if (!cancelled) setStats(doc ?? null);
      })
      .catch((e: unknown) => {
        // Degrade silently: /reports is still useful without this card, and an
        // error banner here would be louder than the thing it is reporting.
        logger.warn('regulars card: lifetime stats read failed', { error: String(e), tenantId });
        if (!cancelled) setStats(null);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  if (stats === 'loading') {
    return (
      <section style={cardStyle}>
        <Skeleton count={2} height={28} radius={'var(--radius-md)'} />
      </section>
    );
  }
  if (stats === null) return null;

  const regulars = stats.repeatCustomerCount ?? 0;
  const customers = stats.customerCount ?? 0;
  // Guarded: a tenant with zero customers must not render "NaN%", and the
  // share is meaningless before anyone has visited at all.
  const share = customers > 0 ? Math.round((regulars / customers) * 100) : 0;

  return (
    <section style={cardStyle}>
      <p style={eyebrowStyle}>{t.t('dashboard.reports.regulars.eyebrow')}</p>

      {regulars === 0 ? (
        <>
          <h3 style={headlineStyle}>{t.t('dashboard.reports.regulars.emptyTitle')}</h3>
          <p style={bodyStyle}>{t.t('dashboard.reports.regulars.emptyBody')}</p>
          <Link to="/loyalty" style={ctaStyle}>
            {t.t('dashboard.reports.regulars.emptyCta')}
          </Link>
        </>
      ) : (
        <>
          <h3 style={headlineStyle}>
            {t.t('dashboard.reports.regulars.headline', { count: regulars })}
          </h3>
          <p style={bodyStyle}>
            {t.t('dashboard.reports.regulars.body', { count: regulars, total: customers, share })}
          </p>
          <Link to="/customers" style={ctaStyle}>
            {t.t('dashboard.reports.regulars.cta')}
          </Link>
        </>
      )}
    </section>
  );
}

const cardStyle: React.CSSProperties = {
  display: 'grid',
  gap: 'var(--space-2)',
  padding: 'var(--space-5)',
  borderRadius: 'var(--radius-lg)',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  boxShadow: 'var(--shadow-1)',
};

const eyebrowStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--color-on-surface-muted)',
};

const headlineStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-h2)',
  fontWeight: 600,
  lineHeight: 1.2,
};

const bodyStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  lineHeight: 1.5,
};

const ctaStyle: React.CSSProperties = {
  justifySelf: 'start',
  marginTop: 'var(--space-2)',
  fontSize: 'var(--text-meta)',
  fontWeight: 600,
  color: 'var(--color-primary)',
  textDecoration: 'none',
};
